// src/MySeasonSection.tsx
//
// „Moje sezóna" na stránce Výsledky (nice-to-have po redesignu, rozhodnuto
// 2. 10. 2026): osobní přehled přihlášeného střelce za vybraný rok – pro
// každou disciplínu, ve které má výsledek. Data počítá utils/mySeason.ts
// z výsledků, které stránka načítá pro roční hodnocení (jen hodnocené
// závody); klepnutí na závod ho otevře v seznamu závodů.
//
// Sekce je ve výchozím stavu sbalená se shrnutím v popisu (výsledky dorazí
// až po seznamu závodů – rozbalená by ho odsunula dolů pod rukou), stav
// rozbalení si pamatuje localStorage.

import React, { useCallback, useState } from "react";
import { Section, SectionHeader } from "./ui/Section";
import { StatTile } from "./ui/Cards";
import { LoadingState } from "./ui/AsyncState";
import { categoryLabel } from "./utils/categories";
import { DISCIPLINES } from "./utils/disciplines";
import { formatCzDate } from "./utils/dates";
import { fmtFixed } from "./utils/formatPoints";
import type { MySeason, MySeasonRaceResult } from "./utils/mySeason";

const OPEN_KEY = "lks_my_season_open";

const readOpen = (): boolean => {
  try {
    return localStorage.getItem(OPEN_KEY) === "1";
  } catch {
    return false;
  }
};

const storeOpen = (open: boolean): void => {
  try {
    localStorage.setItem(OPEN_KEY, open ? "1" : "0");
  } catch {
    // localStorage nedostupné – stav se nezapamatuje
  }
};

/** "2." / "–" */
const place = (rank: number | null | undefined): string => (rank ? `${rank}.` : "–");

/** Umístění v kategorii k řádku závodu: "Muži 2. z 9"; nezařazený "". */
const categoryPlace = (r: MySeasonRaceResult): string =>
  r.categoryId && r.categoryRank && r.categoryOf
    ? `${categoryLabel(r.categoryId)} ${r.categoryRank}. z ${r.categoryOf}`
    : "";

/** "1 závod" / "3 závody" / "5 závodů" */
const racesText = (n: number): string =>
  `${n} ${n === 1 ? "závod" : n >= 2 && n <= 4 ? "závody" : "závodů"}`;

/** Shrnutí do popisu sbalené sekce: „Kulovnice: 3 závody · nejlepší 2. · v roce 5." */
const summaryOf = (seasons: MySeason[]): string =>
  seasons
    .map((season) => {
      const parts = [
        racesText(season.results.length),
        `nejlepší ${place(season.best.rank)}`,
        season.seasonPosition ? `v roce ${place(season.seasonPosition.rank)}` : "",
      ].filter(Boolean);
      const text = parts.join(" · ");
      return seasons.length > 1 ? `${DISCIPLINES[season.discipline].label}: ${text}` : text;
    })
    .join(" / ");

interface DisciplineSeasonProps {
  season: MySeason;
  /** Nadpis disciplíny – jen když se zobrazují obě. */
  showHeader: boolean;
  onOpenRace: (raceId: string) => void;
}

const DisciplineSeason: React.FC<DisciplineSeasonProps> = ({ season, showHeader, onOpenRace }) => {
  const discipline = DISCIPLINES[season.discipline];
  const position = season.seasonPosition;
  const categoryPosition = season.seasonCategoryPosition;

  return (
    <div className="mb-3">
      {showHeader && <SectionHeader title={discipline.label} icon="bi-bullseye" />}
      <div className="row row-cols-2 row-cols-md-4 g-2 mb-2">
        <div className="col">
          <StatTile
            icon="bi-flag-fill"
            tone="brand"
            label="Hodnocené závody"
            value={`${season.results.length} z ${season.ratedRaces}`}
          />
        </div>
        <div className="col">
          <StatTile
            icon="bi-award-fill"
            tone="success"
            label="Nejlepší umístění"
            value={place(season.best.rank)}
            hint={
              <>
                z {season.best.of}
                {/* Název závodu jen na jeden řádek – dlouhé názvy by dlaždici roztáhly */}
                <span className="d-block text-truncate" title={season.best.raceName}>
                  {season.best.raceName}
                </span>
              </>
            }
          />
        </div>
        <div className="col">
          <StatTile
            icon="bi-percent"
            tone="info"
            label="Průměr závodů"
            value={`${fmtFixed(season.averagePercent, 1)} %`}
            hint="z vítěze"
          />
        </div>
        <div className="col">
          <StatTile
            icon="bi-bar-chart-fill"
            tone="neutral"
            label="Roční pořadí"
            value={place(position ? position.rank : null)}
            hint={
              position ? (
                <>
                  z {position.of}
                  {/* Pořadí v kategorii celé (zalomí se mezi slovy) – zkrácené by
                      při větším písmu zmizelo právě číslo */}
                  {categoryPosition ? (
                    <span className="d-block">
                      {categoryLabel(categoryPosition.categoryId)}: {categoryPosition.rank}. z{" "}
                      {categoryPosition.of}
                    </span>
                  ) : null}
                </>
              ) : (
                "zatím bez hodnocení"
              )
            }
          />
        </div>
      </div>

      <div className="list-group">
        {season.results.map((r) => {
          const inCategory = categoryPlace(r);
          return (
            <button
              key={r.raceId}
              type="button"
              className="list-group-item list-group-item-action d-flex align-items-center"
              onClick={() => onOpenRace(r.raceId)}
            >
              <span className="flex-grow-1" style={{ minWidth: 0 }}>
                <span className="d-block" style={{ overflowWrap: "anywhere" }}>
                  {r.raceName}
                </span>
                <span className="d-block small text-body-secondary">
                  {formatCzDate(r.date)}
                  {inCategory ? ` · ${inCategory}` : ""}
                </span>
              </span>
              <span className="text-end flex-shrink-0 ms-2">
                <span className="d-block fw-semibold">
                  {r.rank}. <span className="fw-normal text-body-secondary small">z {r.of}</span>
                </span>
                <span className="d-block small text-body-secondary">
                  {fmtFixed(r.percent, 1)} %
                </span>
              </span>
              <i className="bi bi-chevron-right text-body-secondary ms-2" aria-hidden="true" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export interface MySeasonSectionProps {
  year: number;
  /** Přehledy disciplín, ve kterých má střelec v roce výsledek. */
  seasons: MySeason[];
  /** Hodnocené závody, jejichž výsledky se ještě načítají. */
  pending: number;
  /** Hodnocené závody, jejichž výsledky se nepodařilo načíst. */
  failed: number;
  onRetry: () => void;
  onOpenRace: (raceId: string) => void;
}

export const MySeasonSection: React.FC<MySeasonSectionProps> = ({
  year,
  seasons,
  pending,
  failed,
  onRetry,
  onOpenRace,
}) => {
  const [open, setOpen] = useState(readOpen);
  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    storeOpen(next);
  }, []);

  // Bez vlastních výsledků jen tehdy, když se nějaký závod nenačetl (výsledek
  // mohl být právě v něm) – jinak sekce nemá co ukázat
  if (seasons.length === 0 && failed === 0) return null;
  const incomplete = failed > 0 ? `neúplné – ${failed} ${failed === 1 ? "závod se nenačetl" : failed < 5 ? "závody se nenačetly" : "závodů se nenačetlo"}` : "";
  const collapsedSummary =
    seasons.length > 0
      ? [summaryOf(seasons), incomplete].filter(Boolean).join(" · ")
      : `Výsledky se nepodařilo načíst (${incomplete}).`;
  return (
    <Section
      title={`Moje sezóna ${year}`}
      icon="bi-person-badge"
      description={
        open
          ? "Vaše umístění v hodnocených závodech roku. Klepnutím otevřete výsledky závodu."
          : collapsedSummary
      }
      collapsible
      open={open}
      onOpenChange={handleOpenChange}
    >
      {failed > 0 && (
        <div className="alert alert-warning d-flex align-items-center py-2 small" role="alert">
          <i className="bi bi-exclamation-triangle me-2" aria-hidden="true" />
          <span className="flex-grow-1">
            Výsledky {failed} {failed === 1 ? "závodu" : "závodů"} se nepodařilo načíst –
            přehled je neúplný.
          </span>
          <button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={onRetry}>
            Zkusit znovu
          </button>
        </div>
      )}
      {seasons.map((season) => (
        <DisciplineSeason
          key={season.discipline}
          season={season}
          showHeader={seasons.length > 1}
          onOpenRace={onOpenRace}
        />
      ))}
      {pending > 0 && <LoadingState inline text="Načítám výsledky dalších závodů…" />}
    </Section>
  );
};

export default MySeasonSection;
